import { useState, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../../contexts/auth/Context";
import { useToast } from "../../../contexts/toast/Context";
import { decodeUserInfo } from "../../../utils/user";
import {
  getCurrentUserGymEmailConfigRequest,
  updateCurrentUserGymEmailConfigRequest,
} from "../../requests/admin/gym";
import type { UpdateGymEmailConfigInput } from "../../../utils/zod/admin";
import { PlatformRoleValues } from "../../../utils/zod/common";
import { GYM_EMAIL_CONFIG_QUERY_KEY } from "../../../query/keys";

export const useGymEmailConfig = () => {
  const queryClient = useQueryClient();
  const { token } = useAuth();
  const { showToast } = useToast();
  const { role } = decodeUserInfo(token);
  const [isSaving, setIsSaving] = useState(false);

  const {
    data: emailConfig = null,
    isLoading,
    refetch: fetchConfig,
  } = useQuery({
    queryKey: GYM_EMAIL_CONFIG_QUERY_KEY,
    queryFn: () => getCurrentUserGymEmailConfigRequest(token!),
    enabled: !!token && role === PlatformRoleValues.MANAGER,
  });

  const updateConfig = useCallback(
    async (input: UpdateGymEmailConfigInput) => {
      if (!token || role !== PlatformRoleValues.MANAGER) {
        return false;
      }

      try {
        setIsSaving(true);

        await updateCurrentUserGymEmailConfigRequest(input, token);
        await queryClient.invalidateQueries({
          queryKey: GYM_EMAIL_CONFIG_QUERY_KEY,
        });
        showToast("Configuración guardada correctamente", "success");
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Error al guardar la configuración";
        showToast(errorMessage, "error");
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [token, role, showToast],
  );

  return {
    emailConfig,
    isLoading,
    isSaving,
    updateConfig,
    refreshConfig: fetchConfig,
  };
};
